/** Useful next steps after a partial edit or a request that could not be completed. */
import { useState } from "react";
import type { Change, RequestIssue } from "../api/changes";
import { RECOVERY, SENTENCES } from "../copy/changes";
import { Button } from "../ui";

function grouped(items: RequestIssue[]): RequestIssue[] {
  const seen = new Map<string, RequestIssue>();
  const causes = new Map<string, Set<string>>();
  for (const item of items) {
    const key = item.request.trim().toLowerCase();
    const prior = seen.get(key);
    const reasons = causes.get(key) ?? new Set<string>();
    if (item.reason) reasons.add(item.reason);
    causes.set(key, reasons);
    if (prior) {
      prior.reason = [...reasons].join(" ");
      prior.blockers = [
        ...new Set([...(prior.blockers ?? []), ...(item.blockers ?? [])]),
      ];
    }
    if (!prior)
      seen.set(key, {
        ...item,
        numbers: item.numbers ? [...item.numbers] : undefined,
      });
    else if (item.number && !prior.numbers?.includes(item.number))
      prior.numbers = [
        ...(prior.numbers ?? (prior.number ? [prior.number] : [])),
        item.number,
      ];
  }
  const rows = [...seen.values()];
  // Older failed attempts sometimes include the whole request plus its checklist.
  return rows.filter(
    (item) =>
      item.reason ||
      rows.filter(
        (other) => other !== item && item.request.includes(other.request),
      ).length < 2,
  );
}

function remaining(change: Change): RequestIssue[] {
  const progress = change.progress;
  if (!progress) return [];
  if (change.state === "failed")
    return [
      ...progress.completed.map((item) => ({
        ...item,
        reason: RECOVERY.prepareFailed,
      })),
      ...progress.unfinished,
    ];
  return progress.unfinished;
}

export function continuation(change: Change, design: boolean): string {
  const pending = grouped(remaining(change));
  const detail = pending
    .map((item) => {
      const numbers = item.numbers ?? (item.number ? [item.number] : []);
      return `${numbers.length ? `Scenes ${numbers.join(", ")}: ` : ""}${item.request}`;
    })
    .join("\n");
  const lead = design ? RECOVERY.designRequest : RECOVERY.retryRequest;
  const guidance = [
    ...new Set(
      pending
        .filter((item) => item.blockers?.length)
        .map((item) => item.reason),
    ),
  ].join(" ");
  const text = `${lead}\n${detail || change.text}${guidance ? `\n${RECOVERY.constraints} ${guidance}` : ""}`;
  return text.length <= 2000
    ? text
    : `${lead}\n${RECOVERY.reference.replace("{n}", String(change.n))}`;
}

export function ChangeRecovery({
  change,
  busy,
  onSend,
  onChoose,
  onEdit,
}: {
  change: Change;
  busy: boolean;
  onSend(text: string): void;
  onChoose(text: string): void;
  onEdit(text: string): void;
}) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed || !["applied", "cannot", "failed"].includes(change.state))
    return null;
  const pending = grouped(remaining(change));
  const completed =
    change.state === "applied" ? grouped(change.progress?.completed ?? []) : [];
  if (
    change.state === "applied" &&
    !pending.length &&
    (!change.tasks?.length || !completed.length)
  )
    return null;
  const needsHelp = change.state !== "applied" || pending.length > 0;
  const unsupportedSequence =
    change.error_code === "activity_sequence_unsupported";
  const reachBlocked = pending.some((item) =>
    item.blockers?.includes("reach_too_far"),
  );
  const canDesign = pending.some(
    (item) => item.next_step === "design" || item.next_step === "choose_object",
  );
  const items = (rows: RequestIssue[], unfinished: boolean) => (
    <ul className="m-0 list-disc break-words space-y-1 pl-5 text-sm">
      {rows.map((item, i) => (
        <li key={i}>
          <span>{item.request}</span>
          {item.number ? (
            <span className="text-slate-500">
              {" "}
              · {RECOVERY.scenes} {(item.numbers ?? [item.number]).join(", ")}
            </span>
          ) : null}
          {unfinished &&
          !(
            compact &&
            reasons.includes(item.reason) &&
            item.reason.length <= 280
          ) &&
          (!compact ||
            rows.findIndex((other) => other.reason === item.reason) === i) ? (
            <p className="m-0 text-slate-600">
              {item.reason || RECOVERY.reason}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
  const compact =
    completed.length + pending.length > 3 ||
    [...completed, ...pending].some((item) => item.request.length > 500);
  const reasons = [
    ...new Set(
      pending
        .map((item) => item.reason)
        .filter((reason) => reason && reason !== SENTENCES.task_unverified),
    ),
  ].slice(0, 2);
  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3"
      aria-label={needsHelp ? RECOVERY.title : RECOVERY.results}
      data-testid="change-recovery"
    >
      {compact ? (
        <>
          {completed.length ? (
            <p className="m-0 text-sm">
              {RECOVERY.completedSummary.replace(
                "{count}",
                String(completed.length),
              )}
            </p>
          ) : null}
          {pending.length ? (
            <p className="m-0 text-sm font-semibold">
              {RECOVERY.pendingSummary.replace(
                "{count}",
                String(pending.length),
              )}
            </p>
          ) : null}
          {reasons.map((reason) => (
            <p key={reason} className="m-0 text-sm">
              {reason.length > 280 ? reason.slice(0, 277) + "…" : reason}
            </p>
          ))}
          <details>
            <summary className="cursor-pointer text-sm">
              {RECOVERY.details}
            </summary>
            {completed.length ? items(completed, false) : null}
            {items(pending, true)}
          </details>
        </>
      ) : (
        <>
          {completed.length ? (
            <div>
              <h3 className="m-0 mb-1 text-sm font-semibold">
                {RECOVERY.completed}
              </h3>
              {items(completed, false)}
            </div>
          ) : null}
          {pending.length ? (
            <div>
              <h3 className="m-0 mb-1 text-sm font-semibold">
                {RECOVERY.unfinished}
              </h3>
              {items(pending, true)}
            </div>
          ) : null}
        </>
      )}
      {needsHelp ? (
        <>
          <p className="m-0 text-sm">
            {canDesign ? RECOVERY.designHint : RECOVERY.hint}
          </p>
          <div className="flex flex-wrap gap-2">
            {canDesign ? (
              <Button
                type="button"
                size="sm"
                disabled={busy}
                onClick={() => onSend(continuation(change, true))}
              >
                {RECOVERY.design}
              </Button>
            ) : null}
            {reachBlocked ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    onSend(
                      `${RECOVERY.reference.replace("{n}", String(change.n))}\n${RECOVERY.bringCloserRequest}`,
                    )
                  }
                >
                  {RECOVERY.bringCloser}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  onClick={() =>
                    onSend(
                      `${RECOVERY.reference.replace("{n}", String(change.n))}\n${RECOVERY.approachFirstRequest}`,
                    )
                  }
                >
                  {RECOVERY.approachFirst}
                </Button>
              </>
            ) : null}
            {!unsupportedSequence && !reachBlocked ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() => onSend(continuation(change, false))}
              >
                {RECOVERY.retry}
              </Button>
            ) : null}
            {canDesign ? (
              <a
                href="#film-objects"
                className="tb-btn tb-btn-secondary tb-btn-sm"
                onClick={(event) => {
                  if (busy) {
                    event.preventDefault();
                    return;
                  }
                  onChoose(continuation(change, false));
                }}
                aria-disabled={busy}
              >
                {RECOVERY.choose}
              </a>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => onEdit(change.text)}
            >
              {RECOVERY.edit}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => setDismissed(true)}
            >
              {RECOVERY.keep}
            </Button>
          </div>
        </>
      ) : null}
    </section>
  );
}
